/**
 * Lightweight analytics wrapper — forwards events to Google Analytics 4 (gtag)
 * and our own backend /api/admin/analytics/event endpoint.
 *
 * GA4 is loaded via <script> tag in public/index.html using REACT_APP_GA4_MEASUREMENT_ID.
 * If no measurement ID is configured, GA4 calls are no-ops (safe).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isGaReady = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event. Sent to BOTH GA4 (if configured) and our Mongo analytics.
 * @param {string} name - Event name (snake_case, e.g. "room_view")
 * @param {object} params - Event parameters (must be JSON-safe)
 */
export const trackEvent = (name, params = {}) => {
  if (isGaReady()) {
    try {
      window.gtag('event', name, params);
    } catch (err) {
      // Never let analytics break UX, but do surface for debugging.
      console.warn('[analytics] gtag event failed:', name, err);
    }
  }

  // Mirror key events to backend for admin dashboards
  if (BACKEND_URL && ['room_view', 'booking_click', 'page_view'].includes(name)) {
    fetch(`${BACKEND_URL}/api/admin/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: name,
        page: params.page || window.location.pathname,
        room_id: params.room_id ? String(params.room_id) : null,
        metadata: params,
      }),
    }).catch((err) => console.warn('[analytics] backend event failed:', name, err));
  }
};

export const trackPageView = (path) => {
  trackEvent('page_view', { page: path || window.location.pathname });
};

export const trackRoomView = (roomId, roomName) => {
  trackEvent('room_view', { room_id: roomId, room_name: roomName });
};

export const trackBookingInitiated = (roomName, checkIn, checkOut) => {
  trackEvent('booking_initiated', {
    room_name: roomName,
    check_in: checkIn,
    check_out: checkOut,
  });
};

export const trackBookingCompleted = (roomName, checkIn, checkOut, value) => {
  trackEvent('booking_completed', {
    room_name: roomName,
    check_in: checkIn,
    check_out: checkOut,
    value,
    currency: 'EUR',
  });
};

export const trackLanguageSwitched = (from, to) => {
  trackEvent('language_switched', { from, to });
};

/**
 * Fire abandoned-booking capture to backend.
 * Called when the guest has supplied email + dates but hasn't completed.
 */
export const captureAbandonedBooking = async (payload) => {
  if (!BACKEND_URL) return;
  try {
    await fetch(`${BACKEND_URL}/api/abandoned-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    // Best-effort — don't break checkout UX, but surface for debugging
    console.warn('[analytics] captureAbandonedBooking failed:', err);
  }
};
